import React, {useCallback, useState, useRef, useEffect} from 'react';
import {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

import {BlockType, MarkType} from '../slate.types';
import {isMark} from '../Utils/editor.utils';

import {
  BlockButton,
  MarkButton,
  FontDropdown,
  InOutdentButton,
  LinkButton,
  ImgUploadBtn
} from './ToolbarComponents.react';

type ToolbarButtonType =
  | Exclude<MarkType | BlockType, 'heading-one' | 'heading-two' | 'paragraph' | 'list-item'>
  | 'outdent'
  | 'indent';

interface ButtonAttr {
  icon: IconProp;
  type: ToolbarButtonType;
}

export const AllButtons: ButtonAttr[] = [
  {type: 'heading', icon: 'heading'},
  {type: 'bold', icon: 'bold'},
  {type: 'italic', icon: 'italic'},
  {type: 'underline', icon: 'underline'},
  {type: 'code', icon: 'code'},
  {type: 'superscript', icon: 'superscript'},
  {type: 'subscript', icon: 'subscript'},
  {type: 'numbered-list', icon: 'list-ol'},
  {type: 'bulleted-list', icon: 'list-ul'},
  {type: 'indent', icon: 'indent'},
  {type: 'outdent', icon: 'outdent'},
  {type: 'image', icon: 'image'},
  {type: 'link', icon: 'link'}
];

const maxIndex = AllButtons.length - 1;

const Toolbar = () => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [activeCursor, setActiveCursor] = useState<number | null>(null);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (toolbarRef.current && toolbarRef.current.contains(event.target as Node)) {
      if (event.key === 'ArrowRight') {
        setActiveCursor((oldCursor) => {
          return oldCursor! < maxIndex ? oldCursor! + 1 : 0;
        });
      } else if (event.key === 'ArrowLeft') {
        setActiveCursor((oldCursor) => {
          return oldCursor! > 0 ? oldCursor! - 1 : maxIndex;
        });
      }
    }
  }, []);

  const handleFocus = useCallback(
    (event: KeyboardEvent) => {
      if (toolbarRef.current && toolbarRef.current.contains(event.target as Node)) {
        if (event.key === 'Tab' && activeCursor === null) {
          setActiveCursor(0);
        }
      }
    },
    [activeCursor]
  );

  const selectedProps = {tabIndex: 0};
  const defaultProps = {tabIndex: -1};

  useEffect(() => {
    global.document.addEventListener('keydown', handleKeyPress);
    global.document.addEventListener('keyup', handleFocus);
    return () => {
      global.document.removeEventListener('keydown', handleKeyPress);
      global.document.removeEventListener('keyup', handleFocus);
    };
  }, [handleKeyPress, handleFocus]);

  useEffect(() => {
    if (toolbarRef.current && activeCursor !== null) {
      const selected = toolbarRef.current.children[activeCursor] as HTMLElement;
      if (selected) {
        selected.focus();
      }
    }
  }, [activeCursor]);

  return (
    <div ref={toolbarRef} role='toolbar' className='written-submission__toolbar'>
      {AllButtons.map((button, index) => {
        const isCurrent = activeCursor === index;
        const isFirst = index === 0 && activeCursor === null; // default first button to be tabbable
        const props = isCurrent || isFirst ? selectedProps : defaultProps;
        if (button.type === 'outdent' || button.type === 'indent') {
          return (
            <InOutdentButton icon={button.icon} type={button.type} {...props} key={button.type} />
          );
        }
        if (button.type === 'image') {
          return <ImgUploadBtn {...props} key={button.type} />;
        }
        if (button.type === 'link') {
          return <LinkButton {...props} key={button.type} />;
        }
        if (button.type === 'heading') {
          return <FontDropdown {...props} key={button.type} />;
        }
        if (isMark(button.type)) {
          return <MarkButton icon={button.icon} type={button.type} {...props} key={button.type} />;
        }
        return <BlockButton icon={button.icon} type={button.type} key={button.type} {...props} />;
      })}
    </div>
  );
};
export default Toolbar;
